/**
 * Dev / testing overlay (toggle with the backtick ` key).
 *
 * Lets you spawn any single tank type, jump straight to a layer, and turn on
 * god mode so you can study an enemy's behaviour without fighting it. Using ANY
 * dev action TAINTS the run — the game then disables achievements + profile
 * progress for the rest of that run (so testing can never earn unlocks).
 *
 * Kept deliberately self-contained: it only knows the hook callbacks the game
 * passes in, plus the TANK_TYPES registry for the spawn-button list.
 */

import { TANK_TYPES } from "../config";

export interface DevHooks {
  /** spawn one enemy of this type id near the player */
  spawn(typeId: string): void;
  /** infinite player health on/off */
  setGod(on: boolean): void;
  /** wipe the field and restart on layer n (fresh) */
  jump(layer: number): void;
  /** clear all current enemies */
  clear(): void;
  /** current card-mode cash per kill */
  getCashPerKill(): number;
  /** set card-mode cash per kill (live) */
  setCashPerKill(n: number): void;
  /** is card mode currently unlocked (layer 25 reached, or dev bypass)? */
  getCardUnlock(): boolean;
  /** dev bypass for the layer-25 card-mode unlock */
  setCardUnlock(on: boolean): void;
}

export class DevPanel {
  private el: HTMLDivElement;
  private open = false;

  constructor(private hooks: DevHooks) {
    this.el = document.createElement("div");
    this.el.style.cssText =
      "position:fixed;top:8px;left:8px;z-index:99999;display:none;max-width:300px;" +
      "background:rgba(18,16,12,0.94);color:#e8e0d0;font:11px/1.45 ui-monospace,monospace;" +
      "border:1px solid #6a5630;border-radius:7px;padding:10px 11px;box-shadow:0 6px 22px rgba(0,0,0,0.55);";
    this.build();
    document.body.appendChild(this.el);
    window.addEventListener("keydown", (e) => {
      if (e.code === "Backquote" && !e.repeat && !this.typing(e)) { e.preventDefault(); this.toggle(); }
    });
  }

  private typing(e: KeyboardEvent): boolean {
    const t = e.target as HTMLElement | null;
    return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA");
  }

  toggle(): void { this.open = !this.open; this.el.style.display = this.open ? "block" : "none"; }

  private btn(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.textContent = label;
    b.style.cssText =
      "font:11px ui-monospace,monospace;color:#f0e8d6;background:#3a2f1a;border:1px solid #6a5630;" +
      "border-radius:4px;padding:3px 6px;margin:0;cursor:pointer;";
    b.onmouseenter = () => { b.style.background = "#54421f"; };
    b.onmouseleave = () => { b.style.background = "#3a2f1a"; };
    b.onclick = onClick;
    return b;
  }

  private build(): void {
    const title = document.createElement("div");
    title.textContent = "DEV  ·  ` to close";
    title.style.cssText = "font-weight:bold;color:#ffcf6b;margin-bottom:2px;";
    this.el.appendChild(title);

    const note = document.createElement("div");
    note.textContent = "using anything here disables achievements + progress for this run";
    note.style.cssText = "color:#9c8f78;margin-bottom:8px;font-size:10px;";
    this.el.appendChild(note);

    // god mode
    const godLbl = document.createElement("label");
    godLbl.style.cssText = "display:block;margin-bottom:8px;cursor:pointer;user-select:none;";
    const godCb = document.createElement("input");
    godCb.type = "checkbox";
    godCb.style.cssText = "vertical-align:middle;margin-right:6px;";
    godCb.onchange = () => this.hooks.setGod(godCb.checked);
    godLbl.appendChild(godCb);
    godLbl.appendChild(document.createTextNode("God mode (infinite health)"));
    this.el.appendChild(godLbl);

    // bypass the layer-25 card-mode unlock (for testing card mode without a full climb)
    const cuLbl = document.createElement("label");
    cuLbl.style.cssText = "display:block;margin-bottom:8px;cursor:pointer;user-select:none;";
    const cuCb = document.createElement("input");
    cuCb.type = "checkbox";
    cuCb.checked = this.hooks.getCardUnlock();
    cuCb.style.cssText = "vertical-align:middle;margin-right:6px;";
    cuCb.onchange = () => this.hooks.setCardUnlock(cuCb.checked);
    cuLbl.appendChild(cuCb);
    cuLbl.appendChild(document.createTextNode("Unlock card mode (skip layer 25)"));
    this.el.appendChild(cuLbl);

    // layer jump
    const row = document.createElement("div");
    row.style.cssText = "display:flex;align-items:center;gap:6px;margin-bottom:8px;";
    const lbl = document.createElement("span"); lbl.textContent = "Layer";
    const inp = document.createElement("input");
    inp.type = "number"; inp.min = "1"; inp.value = "46";
    inp.style.cssText = "width:58px;font:11px ui-monospace,monospace;background:#241d10;color:#f0e8d6;border:1px solid #6a5630;border-radius:4px;padding:2px 4px;";
    const go = this.btn("Jump", () => this.hooks.jump(Math.max(1, parseInt(inp.value, 10) || 1)));
    inp.onkeydown = (e) => { if (e.key === "Enter") go.click(); };
    row.append(lbl, inp, go);
    this.el.appendChild(row);

    // card-mode economy dial: cash per kill (live; default 7)
    const cashHd = document.createElement("div");
    cashHd.textContent = "Cash per kill (card mode):";
    cashHd.style.cssText = "margin-bottom:3px;";
    this.el.appendChild(cashHd);
    const cashRow = document.createElement("div");
    cashRow.style.cssText = "display:flex;align-items:center;gap:6px;margin-bottom:9px;";
    const cur = this.hooks.getCashPerKill();
    const slider = document.createElement("input");
    slider.type = "range"; slider.min = "0"; slider.max = "100"; slider.step = "1"; slider.value = String(cur);
    slider.style.cssText = "flex:1;min-width:120px;cursor:pointer;accent-color:#ffcf6b;";
    const num = document.createElement("input");
    num.type = "number"; num.min = "0"; num.value = String(cur);
    num.style.cssText = "width:52px;font:11px ui-monospace,monospace;background:#241d10;color:#ffcf6b;font-weight:bold;border:1px solid #6a5630;border-radius:4px;padding:2px 4px;";
    const apply = (v: number) => {
      const n = Math.max(0, Math.round(v) || 0);
      this.hooks.setCashPerKill(n);
      if (slider.value !== String(n)) slider.value = String(n);
      if (num.value !== String(n)) num.value = String(n);
    };
    slider.oninput = () => apply(parseInt(slider.value, 10));
    num.oninput = () => apply(parseInt(num.value, 10));
    cashRow.append(slider, num);
    this.el.appendChild(cashRow);

    // spawn one of each type
    const sh = document.createElement("div");
    sh.textContent = "Spawn one near player:";
    sh.style.cssText = "margin-bottom:4px;";
    this.el.appendChild(sh);
    const grid = document.createElement("div");
    grid.style.cssText = "display:flex;flex-wrap:wrap;gap:4px;margin-bottom:9px;";
    for (const t of Object.values(TANK_TYPES)) grid.appendChild(this.btn(t.name, () => this.hooks.spawn(t.id)));
    this.el.appendChild(grid);

    const clear = this.btn("Clear all enemies", () => this.hooks.clear());
    clear.style.width = "100%";
    this.el.appendChild(clear);
  }
}
